import { useCallback } from 'react';

import { useSelector } from '@xstate5/react';
import { type ActorRefFrom, type SnapshotFrom } from 'xstate5';

import { type createCounterMachine } from './counter-machine';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A small hook to interact with counter state machine in React.
 * Expects counter machine actor ref as an argument.
 */
export function useCounterMachineInterpreter(interpreter: CounterMachineActorRef) {
  //
  // ─── Helpers ─────────────────────────────────────────────────────────

  const incrementCounter = useCallback(() => {
    interpreter.send({ type: 'INCREMENT' });
  }, [interpreter]);

  const decrementCounter = useCallback(() => {
    interpreter.send({ type: 'DECREMENT' });
  }, [interpreter]);

  const resetCounter = useCallback(() => {
    interpreter.send({ type: 'RESET' });
  }, [interpreter]);

  // ─── Data ────────────────────────────────────────────────────────────

  const counterValue = useSelector(interpreter, counterValueSelector);
  const isReady = useSelector(interpreter, isReadySelector);

  // ─────────────────────────────────────────────────────────────────────

  return {
    isReady,
    counterValue,
    incrementCounter,
    decrementCounter,
    resetCounter,
  };
}

// ─── Selectors ───────────────────────────────────────────────────────────────

function isReadySelector(snapshot: CounterMachineSnapshot) {
  return snapshot.matches('ready');
}

function counterValueSelector(snapshot: CounterMachineSnapshot) {
  return snapshot.context.value;
}

// ─── Types ───────────────────────────────────────────────────────────────────

type CounterMachineActorRef = ActorRefFrom<ReturnType<typeof createCounterMachine>>;

type CounterMachineSnapshot = SnapshotFrom<CounterMachineActorRef>;
